import logger from './utils/logger';
import { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Daily from '@daily-co/daily-js';
import { DailyProvider } from '@daily-co/daily-react';
import './styles.css';
import { safeLocalStorage } from './utils/safeStorage';
import { logCompatibilityReport } from './utils/compatibilityCheck';
import { t } from './i18n';
import { useBrowserLanguage } from './hooks/useBrowserLanguage';
import { MobileInstallBanner } from './components/MobileInstallBanner';
import { initializeInstallPrompt, shouldShowInstallPrompt } from './utils/mobileInstallation';
import { isRecorderWindow } from './recorder/recorderProtocol';

// Lazy-load VideoRoom to avoid blocking the initial React scheduler task
const VideoRoom = lazy(() => import('./VideoRoom'));
// Headless recorder client (Version 2.md, D10) — never loaded in a normal session
const RecorderApp = lazy(() =>
  import('./recorder/RecorderApp').then(m => ({ default: m.RecorderApp })),
);

// Lazy singleton — avoids module-level initialization while still being safe
// under React Strict Mode (double-mount creates the object only once)
let _callObject: ReturnType<typeof Daily.createCallObject> | null = null;
function getCallObject() {
  if (!_callObject) {
    _callObject = Daily.createCallObject({
      // Manual track subscriptions (Version 2.md, D4). useTrackSubscriptions is
      // then the only thing that turns tracks on; below ~30 participants it
      // subscribes to everyone, so behaviour matches the automatic mode without
      // the join-time renegotiation storm.
      //
      // This has to be set here, not toggled later: Daily's own docs say to set
      // it at creation time to prevent subscriptions at call initiation, and
      // flipping it mid-call would renegotiate everything at the worst moment.
      subscribeToTracksAutomatically: false,
    });
    if (typeof (_callObject as any).setMaxListeners === 'function') {
      (_callObject as any).setMaxListeners(30);
    }

    // Protect against race-condition rejections from startRemoteParticipantsAudioLevelObserver
    // when multiple hooks (e.g. daily-react useAudioLevelObserver and useDominantSpeaker)
    // start observing concurrently.
    const origStartObserver = _callObject.startRemoteParticipantsAudioLevelObserver;
    if (origStartObserver) {
      let startObserverPromise: Promise<any> | null = null;
      _callObject.startRemoteParticipantsAudioLevelObserver = async function (...args: Parameters<typeof origStartObserver>) {
        if (_callObject?.isRemoteParticipantsAudioLevelObserverRunning?.()) {
          return;
        }
        if (startObserverPromise) {
          return startObserverPromise;
        }
        try {
          startObserverPromise = origStartObserver.apply(this, args);
          return await startObserverPromise;
        } catch (err: any) {
          const errMsg = err?.error || err?.message || String(err);
          if (
            typeof errMsg === 'string' &&
            (errMsg.includes('already') || errMsg.includes('stopRemoteParticipantsAudioLevelObserver'))
          ) {
            return;
          }
          throw err;
        } finally {
          startObserverPromise = null;
        }
      };
    }
  }
  return _callObject;
}



function AppContent() {
  const lang = useBrowserLanguage();
  const [showInstallPrompt, setShowInstallPrompt] = useState(() => shouldShowInstallPrompt());

  // Initialize mobile installation prompt
  useEffect(() => {
    const registerComplete = initializeInstallPrompt();
    registerComplete(() => setShowInstallPrompt(false));
  }, []);

  // Clean up old localStorage keys from previous second screen implementation
  useEffect(() => {
    const keysToRemove = [
      'secondScreenEnabled',
      'secondScreenPosition',
      'secondScreenSize',
      'secondScreenFullscreen'
    ];
    
    keysToRemove.forEach(key => {
      safeLocalStorage.removeItem(key);
    });
  }, []);



  // Check for HTTPS requirement and log compatibility (once on mount)
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.protocol === 'http:' && window.location.hostname !== 'localhost') {
      logger.warn('⚠️ WebRTC requires HTTPS in production. Camera and microphone access will fail over HTTP.');
    }

    logCompatibilityReport(lang, t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The recorder is selected by query flag rather than by path, so it works
  // regardless of hosting rewrite rules, and inherits the room parameters from
  // the URL the master was already on.
  if (isRecorderWindow()) {
    return <Suspense fallback={null}><RecorderApp /></Suspense>;
  }

  // Show install prompt before VideoRoom if needed
  if (showInstallPrompt) {
    return <MobileInstallBanner lang={lang} onComplete={() => setShowInstallPrompt(false)} />;
  }

  return (
    <Routes>
      <Route path="/" element={<Suspense fallback={null}><VideoRoom /></Suspense>} />
    </Routes>
  );
}

function App() {
  const [callObject] = useState(getCallObject);
  return (
    <DailyProvider callObject={callObject}>
      <Router>
        <AppContent />
      </Router>
    </DailyProvider>
  );
}

export default App;
